<template>
  <div class="content-wrapper">
    <app-breadcrumb
        :page-title="pageTitle"
        :directory="$t('datatables')"
        :icon="'grid'"
    />
    <app-table :id="tableId" :options="options" @action="getListAction"/>

    <template v-if="confirmEmploymentModal">
      <app-modal
          :modal-id="confirmEmploymentModalId"
          modal-alignment="top"
          modal-size="default"
          @close-modal="closeModal(confirmEmploymentModalId)"
      >
        <template slot="header">
          <h5 class="modal-title">{{ $t("Add Applicant To Employment") }}</h5>
          <button
              type="button"
              class="close outline-none"
              data-dismiss="modal"
              aria-label="Close"
              @click="confirmEmploymentModal = false"
          >
            <span><app-icon :name="'x'"></app-icon></span>
          </button>
        </template>
        <template slot="body">
          <div v-if="fetchingForms" class="text-center p-5">
            <app-preloader />
          </div>
          <div v-else-if="screeningForms.length > 0">
            <p>{{ $t('The following forms/contracts will be assigned to the applicant:') }}</p>
            <ul class="list-group">
              <li v-for="form in screeningForms" :key="form.id" class="list-group-item d-flex align-items-center">
                <input 
                  type="checkbox" 
                  :value="form.form_revision_id" 
                  v-model="selectedEmploymentForms"
                  class="mr-3"
                  style="width: 18px; height: 18px; cursor: pointer;"
                >
                {{ form.title }}
              </li>
            </ul>
          </div>
          <div v-else class="text-center">
            <p class="text-danger">{{ $t('No employment forms found. Please add forms to employment contracts first then proceed next.') }}</p>
            <a href="/admin/documentlibrary/mydocument" class="btn btn-primary">{{ $t('Go to Document Library') }}</a>
          </div>
        </template>
        <template slot="footer">
          <button
              v-if="screeningForms.length > 0"
              type="button"
              class="btn btn-success mr-2"
              @click.prevent="addApplicantToEmploymentConfirmed">
            {{ $t("Proceed") }}
          </button>
          <button
              type="button"
              class="btn btn-secondary mr-2"
              data-dismiss="modal"
              @click="closeModal(confirmEmploymentModalId)">
            {{ $t("Cancel") }}
          </button>
        </template>
      </app-modal>
    </template>

    <template v-if="downloadEmploymentContractsModal">
      <app-modal
          :modal-id="downloadEmploymentContractsModalId"
          modal-alignment="top"
          modal-size="default"
          @close-modal="closeModal(downloadEmploymentContractsModalId)"
      >
        <template slot="header">
          <h5 class="modal-title">{{ $t("Download Employment Forms") }}</h5>
          <button
              type="button"
              class="close outline-none"
              data-dismiss="modal"
              aria-label="Close"
              @click="downloadEmploymentContractsModal = false"
          >
            <span><app-icon :name="'x'"></app-icon></span>
          </button>
        </template>
        <template slot="body">
          <div v-if="fetchingForms" class="text-center p-5">
            <app-preloader />
          </div>
          <div v-else-if="screeningForms.length > 0" class="p-3">
            <table class="table table-borderless" style="width: 100%;">
              <thead>
                <tr style="border-bottom: 2px solid #e9ecef;">
                  <th style="padding: 12px; font-weight: 600; color: #000;">{{ $t('Form') }}</th>
                  <th style="padding: 12px; font-weight: 600; color: #000; text-align: center;">{{ $t('Status') }}</th>
                  <th style="padding: 12px; font-weight: 600; color: #000; text-align: center;">{{ $t('Reminder') }}</th>
                  <th style="padding: 12px; font-weight: 600; color: #000; text-align: center;">{{ $t('Counter Sign') }}</th>
                  <th style="padding: 12px; font-weight: 600; color: #000; text-align: center;">{{ $t('Download') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(form, index) in screeningForms" :key="form.id" :style="index % 2 === 0 ? 'background-color: #f8f9fa;' : 'background-color: #ffffff;'">
                  <td style="padding: 15px; vertical-align: middle;">
                    <a href="javascript:void(0)" @click="downloadContract(form)" style="font-size: 14px; color: #4e73df; font-weight: 500; text-decoration: none;" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">
                      {{ form.title }}
                    </a>
                  </td>
                   <td style="padding: 15px; text-align: center; vertical-align: middle;">
                    <span style="font-size: 14px;" :style="{ color: (form.submission_status !== 'Not submitted' && form.submission_status !== 'not submitted') ? '#28a745' : '#6c757d', fontWeight: (form.submission_status !== 'Not submitted' && form.submission_status !== 'not submitted') ? '600' : '400' }">
                      {{ form.submission_status === 'Not submitted' ? $t('Not submitted') : $t($t(form.submission_status).charAt(0).toUpperCase() + $t(form.submission_status).slice(1)) }}
                    </span>
                  </td>
                  <td style="padding: 15px; text-align: center; vertical-align: middle;">
                    <button 
                      class="btn btn-warning" 
                      @click="sendReminderEmail(form)"
                      style="background-color: #ffc107; border: none; padding: 8px 20px; font-size: 14px; border-radius: 4px; color: #000; font-weight: 500;">
                      {{ $t('Reminder') }}
                    </button>
                  </td>
                  <td style="padding: 15px; text-align: center; vertical-align: middle;">
                    <button 
                      class="btn btn-primary ml-1" 
                      @click="counterSign(form)"
                      :style="{ backgroundColor: (form.submission_status === 'Not submitted' || form.submission_status === 'not submitted') ? '#b0c4de' : '#4e73df', border: 'none', padding: '8px 20px', fontSize: '14px', borderRadius: '4px', color: '#fff', fontWeight: '500', cursor: (form.submission_status === 'Not submitted' || form.submission_status === 'not submitted') ? 'not-allowed' : 'pointer' }">
                      {{ $t('Counter Sign') }}
                    </button>
                  </td>
                  <td style="padding: 15px; text-align: center; vertical-align: middle;">
                    <input 
                      type="checkbox" 
                      :id="'form-checkbox-' + form.id"
                      v-model="selectedForms"
                      :value="form.form_revision_id"
                      style="width: 20px; height: 20px; cursor: pointer;"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center p-5 text-secondary">
            <p style="font-size: 16px;">{{ $t('No employment forms have been assigned to this applicant yet.') }}</p>
          </div>
        </template>
        <template slot="footer">
          <button
              v-if="screeningForms.length > 0"
              type="button"
              class="btn btn-success mr-2"
              @click="downloadSelectedForms">
            {{ $t("Download Report") }} ({{ selectedForms.length }})
          </button>
          <button
              type="button"
              class="btn btn-secondary mr-2"
              data-dismiss="modal"
              @click="closeModal(downloadEmploymentContractsModalId)">
            {{ $t("Close") }}
          </button>
        </template>
      </app-modal>
    </template>

    <task-endorse
        v-if="isTaskEndorseModalActive"
        modal-id="endorse-Modal"
        :taskId="endorseTaskId"
        :formData="endorseFormData"
        @close-modal="isTaskEndorseModalActive = false"
        @afterSubmitDone="downloadEmploymentContracts"
    />
  </div>
</template>

<script>
import CoreLibrary from "../../../../../core/helpers/CoreLibrary.js";
import {TableHelpers} from "../../Demo/Tables/mixins/TableHelpers";
import {COMPLETED_SCREENING, CHANGE_SCREENING_STATUS, ADD_TO_EMPLOYMENT, GET_SCREENING_FORMS, GET_ASSIGNED_SCREENING_FORMS, APPLICANT_ENDORSEMENT} from '../../../../Config/ScreeningAppUrl'
import {ModalMixin} from "@/app/Mixins/ModalMixin";
import {FormMixin} from "@/core/mixins/form/FormMixin";
import {Candidate} from "@Views/Screening/utility/apiAgent";
import {mergeDateTime} from "@Views/Screening/utility/common/candidate";
import {axiosGet, axiosPost} from "@/app/Helpers/AxiosHelper";
import {GET_COMPANY_USERS} from "@/app/Config/ApiUrl";
import {APPLICANT_FILES} from "@/app/Config/ScreeningAppUrl";
import * as actions from "@/app/Config/ApiUrl";
import AxiosFunction from "@/core/helpers/axios/AxiosFunction";


export default {
  extends: CoreLibrary,
  name: "ScreeningCompleted",
  mixins: [TableHelpers, FormMixin, ModalMixin],
  props: {
    active: Number,
    customerCompany: {},
  },
  data() {
    return {
      pageTitle: this.getPageTitle(),
      id: 0,
      tableId: "completed_screenings",
      rowData: {},
      screeningChecklists: [],
      localChecklists: [],
      confirmEmploymentModalId: 'confirm_employment_modal_completed',
      downloadEmploymentContractsModalId: 'download_employment_contracts_modal',
      screeningForms: [],
      selectedForms: [],
      confirmEmploymentModal: false,
      downloadEmploymentContractsModal: false,
      fetchingForms: false,
      isTaskEndorseModalActive: false,
      endorseTaskId: 0,
      endorseFormData: {},

      options: {
        name: "AdvanceTable",
        url: COMPLETED_SCREENING,
        showHeader: true,
        columns: [
          {
            title: this.$t("Applicant Id"),
            type: "custom-html",
            key: "id",
            sortable: true,
            modifier: (value, row) => {
              const baseId = 10000;
              const applicantId = baseId + value;

              // Optional: Add company prefix or logic
              // e.g., if company_id = 3, you could show A03-10002
              const companyPrefix = row.company_id ? `C${String(row.company_id).padStart(2, '0')}-` : '';

              return `<span>${companyPrefix}${applicantId}</span>`;
            },
          },
          {
            title: this.$t("Employee Id"),
            type: "custom-html",
            key: "employee_id",
            sortable: true,
            modifier: (value, row) => {
              return `<span>${row.applicant?.user_id ?? '-'}</span>`;
            },
          },
          {
            title: this.$t("name"),
            type: "custom-html",
            key: "applicant",
            sortable: true,
            modifier: (value) => {
              return `<span>${this.$t(value.full_name)}</span>`;
            },
          },

          // {
          //   title: this.$t("Application Received"),
          //   type: "custom-html",
          //   key: "created_at",
          //   modifier: (value) => {
          //     var date = moment(value).format("DD-MM-YYYY");
          //     return `<span>${this.$t(date)}</span>`;
          //   },
          // },
          {
            title: this.$t("Screening Start Date"),
            type: "custom-html",
            key: "start_date",
            sortable: true,
            modifier: (value) => {
              var date = moment(value).format("DD-MM-YYYY");
              return `<span>${this.$t(date)}</span>`;
            },
          },
          {
            title: this.$t("Screening Completion Date"),
            type: "custom-html",
            key: "completed_at",
            sortable: true,
            modifier: (value) => {
              var date = moment(value).format("DD-MM-YYYY");
              return `<span>${this.$t(date)}</span>`;
            },
          },
          {
            title: this.$t("status"),
            type: "custom-html",
            key: "status",
            modifier: (value) => {
              return `<span>${this.$t(value)}</span>`;
            },
          },
          {
            title: this.$t("Screening Type"),
            type: "custom-html",
            key: "screening_type",
            modifier: (value) => {
              return `<span>${this.$t(value??'Internal')}</span>`;
            },
          },
        ],
        actions: [
          {
            title: this.$t("view"),
            icon: "eye",
            type: "none",
            color: "#4A90E2", // Blue
          },
          {
            title: this.$t("download_employment_contracts"),
            icon: "arrow-down-circle",
            type: "none",
            color: "#417505", // Dark Green
          },
          {
            title: this.$t("revert_to_screening"),
            icon: "corner-up-left",
            type: "none",
            color: "#F5A623", // Orange
          },
          {
            title: this.$t("add_applicant_to_employment"),
            icon: "plus",
            type: "none",
            color: "#00BFFF", // Cyan
          },
          {
            title: this.$t("download_full_screening_report"),
            icon: "download",
            type: "none",
            color: "#7ED321", // Green
          },
        ],
        filters: [
          { key: 'start_date', title: this.$t('Screening Start Date'), type: 'range-picker', initValue: '' },
          { key: 'completed_at', title: this.$t('Screening Completion Date'), type: 'range-picker', initValue: '' },
        ],
        showFilter: true,
        showSearch: true,
        showCount: true,
        showClearFilter: true,
        paginationType: "pagination",
        responsive: true,
        rowLimit: 30,
        showAction: true,
        orderBy: "desc",
        actionType: "default",
      },
      vetting: {},
      users: [],
      select_user: '',
      applicantFiles: [],
      selectedEmploymentForms: [],
    };
  },
  created() {
    this.options.columns = [...this.options.columns, this.actionObj];
  },
  methods: {
    addApplicantToEmployment(){
      this.fetchScreeningForms();
    },
    fetchScreeningForms() {
      this.fetchingForms = true;
      this.confirmEmploymentModal = true;
      this.downloadEmploymentContractsModal = false;
      axiosGet(GET_SCREENING_FORMS)
          .then(({data}) => {
            this.screeningForms = data;
            // Select all by default
            this.selectedEmploymentForms = data.map(form => form.form_revision_id);
          })
          .catch((error) => {
            console.error(error);
            const message = error.response && error.response.data && error.response.data.message 
              ? error.response.data.message 
              : "Failed to fetch screening forms.";
            this.$toastr.e(message);
          })
          .finally(() => {
            this.fetchingForms = false;
          });
    },
    downloadEmploymentContracts() {
      this.selectedForms = [];
      this.fetchingForms = true;
      this.downloadEmploymentContractsModal = true;
      this.confirmEmploymentModal = false;
      axiosGet(GET_ASSIGNED_SCREENING_FORMS + '?applicant_id=' + this.rowData.applicant_id)
          .then(({data}) => {
            this.screeningForms = data.forms;
          })
          .catch((error) => {
            console.error(error);
            const message = error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : "Failed to fetch screening forms.";
            this.$toastr.e(message);
          })
          .finally(() => {
            this.fetchingForms = false;
          });
    },
    downloadContract(form) {
      window.open(`/generate-candidate-screening-report/single_form?applicant_id=${this.rowData.applicant_id}&form_revision_id=${form.form_revision_id}`, '_blank');
    },
    sendReminderEmail(form) {
      const applicantName = this.rowData.applicant?.full_name || 'Applicant';
      this.$toastr.s(this.$t('Reminder email being sent to ') + applicantName);
      
      const payload = {
        url: APPLICANT_ENDORSEMENT,
        data: {
          id: this.rowData.id,
          message: `Dear ${applicantName},\n\nThis is a reminder to submit the following form: ${form.title}\n\nPlease complete and submit this form as soon as possible.\n\nThank you.`
        },
      }
      this.axiosPost(payload)
      .then((response) => {
        // Success
      }).catch((err) => {
        this.$toastr.e(err.response?.data?.message || 'Failed to send reminder email');
      });
    },
    counterSign(form) {
      if (form.submission_status === 'Not submitted' || form.submission_status === 'not submitted') {
        this.$toastr.e(this.$t('Please submit the form first.'));
        return;
      }
      if (!form.task_id) {
        this.$toastr.e(this.$t('This form is not assigned to a task. Cannot counter-sign.'));
        return;
      }

      if (form.is_counter_signed) {
        if (!confirm(this.$t('A counter-signature already exists. Do you want to update it?'))) {
          return;
        }
      }

      this.closeModal(this.downloadEmploymentContractsModalId);

      this.endorseTaskId = form.task_id;
      this.endorseFormData = {
        ...form,
        task_id: form.task_id,
        form_id: form.form_revision_id,
        applicant_id: this.rowData.applicant_id,
        company_id: this.rowData.company_id,
        user_id: this.rowData.applicant?.user_id
      };
      this.isTaskEndorseModalActive = true;
    },
    downloadSelectedForms() {
      if (this.selectedForms.length === 0) {
        this.$toastr.w(this.$t('Please select at least one form to download'));
        return;
      }

      // Check if any selected form is not submitted
      const hasNonSubmittedSelected = this.screeningForms.some(form => 
        this.selectedForms.includes(form.form_revision_id) && 
        (form.submission_status === 'Not submitted' || form.submission_status === 'not submitted')
      );

      // Determine which forms to download
      let formsToDownload = this.selectedForms;

      // Show confirmation if non-submitted forms are selected
      if (hasNonSubmittedSelected) {
        const confirmed = window.confirm(this.$t('Are you sure you want to download? Some forms are not submitted yet and will show empty templates.'));
        
        if (!confirmed) {
          // User clicked No/Cancel - download only submitted forms
          formsToDownload = this.screeningForms
            .filter(form => 
              this.selectedForms.includes(form.form_revision_id) && 
              form.submission_status !== 'Not submitted' && 
              form.submission_status !== 'not submitted'
            )
            .map(form => form.form_revision_id);
          
          // Check if there are any submitted forms left
          if (formsToDownload.length === 0) {
            this.$toastr.w(this.$t('No submitted forms selected. Please submit the forms first.'));
            return;
          }
        }
      }

      // Generate single combined report with selected forms
      const formRevisionIds = formsToDownload.join(',');
      window.open(`/generate-candidate-screening-report/single_form?applicant_id=${this.rowData.applicant_id}&form_revision_id=${formRevisionIds}`, '_blank');

      // Clear selections and close modal after download
      setTimeout(() => {
        this.selectedForms = [];
        this.closeModal(this.downloadEmploymentContractsModalId);
      }, 1000);
    },
    requestApplicantEndorsement(){
      const applicantName = this.rowData.applicant?.full_name || 'Applicant';
      this.$toastr.s(this.$t('Endorsement Request being sent to ') + applicantName);
      
      this.downloadEmploymentContractsModal = false;
      this.closeModal(this.downloadEmploymentContractsModalId);

      const payload = {
        url: APPLICANT_ENDORSEMENT,
        data: {
          id:this.rowData.id,
          message: this.notificationMessage
        },
      }
      this.axiosPost(payload)
      .then((response) => {
        // Success handled instantly above for better UX
      }).catch((err) => {
        this.$toastr.e(err.response.data.message);
      }).finally(() => {
        this.$hub.$emit('reload-' + this.tableId);
      });
    },
    addApplicantToEmploymentConfirmed(){
      const payload = {
        url: ADD_TO_EMPLOYMENT,
        data: {
          ...this.rowData,
          selected_forms: this.selectedEmploymentForms
        },
      }
      this.axiosPost(payload)
          .then((response) => {
            this.$toastr.s(response.data.message)
          }).catch((err) => {

        this.$toastr.e(err.response.data.message);
      }).finally(() => {
        this.closeModal(this.confirmEmploymentModalId);
      })
    },
    closeModal(modalId) {
      $(`#${modalId}`).modal('hide');
      this.confirmEmploymentModal = false;
      this.downloadEmploymentContractsModal = false;
    },
    changeScreeningStatus(){
      const postData = {
        url: CHANGE_SCREENING_STATUS,
        data: {
        applicantId: this.rowData.applicant_id,
        screeningScheduleId: this.rowData.id,
        }
      }
      AxiosFunction.responseToCallback(
          AxiosFunction.axiosPost(postData),  // The promise
          (success, data) => {
            if (success) {

              this.$toastr.s(data.message);
              this.$hub.$emit('reload-' + this.tableId);
            }
          },
          (success, errorData) => {
            if (!success) {
              this.$toastr.e(errorData.message || "Failed to submit form.");
            }
          }
      );
    },
    openCandidateDetail(rowData) {
      if (!rowData || !rowData.company_id || !rowData.applicant_id) {
        console.error("Invalid rowData:", rowData);
        return;
      }

      const url = `/candidate-detail/${rowData.company_id}/${rowData.applicant_id}?tab=Upload%20Documents`;
      window.open(url, "_blank");
    },
    mergeDateTime,
    getPageTitle() {
      let pageTitle = 'Completed Screening';
      let customerCompany = this.customerCompany;
      if (customerCompany) {
        pageTitle = 'Screening For ' + customerCompany.company_name;
      }
      return pageTitle;
    },
    getListAction(rowData, actionObj, active) {
      this.rowData = rowData;
      if (actionObj.title == this.$t("view")) {
        window.open(`/candidate-detail/${rowData.company_id}/${rowData.applicant_id}`, '_blank' );
      }else if (actionObj.title == this.$t("revert_to_screening")) {
          this.changeScreeningStatus();

      } else if (actionObj.title == this.$t("add_applicant_to_employment")) {
        if (this.rowData.applicant?.user_id) {
          this.$toastr.i(this.$t("Applicant is already an employee"));
          return;
        }
        this.fetchScreeningForms();
      }else if (actionObj.title == this.$t("download_employment_contracts")) {
        this.downloadEmploymentContracts();
      }else if (actionObj.title == this.$t("download_application_form")) {
        window.open('/generate-candidate-screening-report/applicationForm?applicant_id='+rowData.applicant_id, '_blank');
      }else if (actionObj.title == this.$t("download_full_screening_report")) {
        window.open('/generate-candidate-screening-report/full_screening?applicant_id='+rowData.applicant_id, '_blank');
      }


    }
    },

  computed:{
    today(){
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, '0');
      const mm = String(today.getMonth() + 1).padStart(2, '0'); // January is 0!
      const yyyy = today.getFullYear();

      return `${dd}-${mm}-${yyyy}`;
    }
  },

};
</script>